import { getSyncHook, regHook, setHooks } from "./regHooks";

export interface ReadyStateType {
  loading: boolean; // 加载中
  data: any; // 已加载数据
  error: any; // 加载失败报错
};

export interface ReadyCallbackType {
  success: (data: any) => void;
  failed: (error: any) => void;
}

const readyStateQueue: {
  [key: string]: ReadyStateType;
} = {};

/**
 * 获取ready缓存的状态
 * @param {String} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：initFormReady
 * @returns ready缓存的状态
 */
export function getReadyState(app: string, name: string) {
  const readyName = `${app}.${name}`;
  let readyState = readyStateQueue[readyName];
  if (!readyState) {
    // 初始化 ready state
    readyState = {
      loading: true,
      data: undefined,
      error: undefined,
    };
    readyStateQueue[readyName] = readyState;
  }
  return readyState;
}

/**
 * 注册ready钩子
 * @param {String} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：initFormReady
 * @param {ReadyCallbackType} callback ready成功失败回调钩子 
 */
export function regReadyHook(app: string, name: string, callback: ReadyCallbackType) {
  const successHookName = `${name}.success`;
  const failedHookName = `${name}.failed`;
  const { success, failed } = callback || {};
  // 获取 ready state
  const readyState = getReadyState(app, name);
  // 根据 ready state 状态来处理 ready 钩子
  // 如果 ready 正在执行
  if (readyState.loading) {
    // 将 成功回调 推入栈
    if (success) {
      regHook(app, successHookName, success);
    }
    // 将 失败回调 推入栈
    if (failed) {
      regHook(app, failedHookName, failed);
    }
  }
  // 如果 ready 执行完成
  else if (readyState.data) {
     success?.(readyState.data);
  }
  // 如果 ready 执行失败
  else if (readyState.error) {
     failed?.(readyState.error);
  }
}

/**
 * 获取ready成功回调钩子
 * @param {String} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：initFormReady
 * @returns ready成功回调钩子
 */
export function getReadySuccessHook<Data = any> (app: string, name: string): ((data: Data) => any) {
  return (data: Data) => {
    // 获取 ready state
    const readyState = getReadyState(app, name);
    if (readyState?.loading) {
      // 设置loading状态
      readyState.loading = false;
      // 保存已加载的结果
      readyState.data = data;
      // 执行已缓存的钩子
      getSyncHook(app, `${name}.success`)?.(data);
      // 清除缓存的钩子
      setHooks(app, `${name}.success`, []);
      setHooks(app, `${name}.failed`, []);
    }
  }
}

/**
 * 获取ready失败回调钩子
 * @param {String} app 应用名称，形如：@weapp/ui, @weapp/utils
 * @param {string} name 钩子名称，形如：initFormReady
 * @returns ready失败回调钩子
 */
export function getReadyFailedHook<ErrorType = Error> (app: string, name: string): ((error: ErrorType) => any) {
  return (error: ErrorType) => {
    // 获取 ready state
    const readyState = getReadyState(app, name);
    if (readyState?.loading) {
      // 设置loading状态
      readyState.loading = false;
      // 保存加载错误
      readyState.error = error;
      // 执行已缓存的钩子
      getSyncHook(app, `${name}.failed`)?.(error);
      // 清除缓存的钩子
      setHooks(app, `${name}.success`, []);
      setHooks(app, `${name}.failed`, []);
    }
  }
}
